import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { monthLabel } from '../mocks/wipAiFixture'
import { I } from './Icons'

const Wrap = styled.div`
  position: relative;
`

const PillSelect = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 34px;
  padding: 0 12px;
  border-radius: 8px;
  border: 1px solid ${p => p.theme.colors.semantic.border};
  background: ${p => p.theme.colors.semantic.surface};
  color: ${p => p.theme.colors.semantic.textPrimary};
  font-size: 13px;
  font-weight: 500;
  max-width: 220px;
  &:hover:not(:disabled) {
    background: ${p => p.theme.colors.semantic.surfaceMuted};
  }
  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
  svg {
    width: 14px;
    height: 14px;
    opacity: 0.6;
    flex-shrink: 0;
  }
`

const Cap = styled.span`
  color: ${p => p.theme.colors.semantic.textMuted};
  font-size: 11px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  font-weight: 600;
  flex-shrink: 0;
`

const Val = styled.span`
  font-weight: 600;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 0;
`

const Dropdown = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  width: min(260px, 88vw);
  max-height: 320px;
  display: flex;
  flex-direction: column;
  background: ${p => p.theme.colors.semantic.surface};
  border: 1px solid ${p => p.theme.colors.semantic.border};
  border-radius: 10px;
  box-shadow: ${p => p.theme.colors.semantic.shadowLg};
  z-index: 200;
  overflow: hidden;
`

const List = styled.div`
  overflow-y: auto;
  max-height: 320px;
`

const Row = styled.button`
  display: block;
  width: 100%;
  text-align: left;
  padding: 10px 14px;
  border: none;
  border-bottom: 1px solid ${p => p.theme.colors.semantic.border};
  background: ${p => (p.$active ? p.theme.colors.semantic.surfaceMuted : 'transparent')};
  cursor: pointer;
  font-size: 13px;
  font-weight: ${p => (p.$active ? 600 : 500)};
  color: ${p => p.theme.colors.semantic.textPrimary};
  font-family: ${p => p.theme.fontFamily.sans.join(', ')};
  &:hover {
    background: ${p => p.theme.colors.semantic.lilacTint};
  }
`

const WipAiMonthSelect = ({ months = [], selectedMonth, onSelectMonth, disabled }) => {
  const [open, setOpen] = useState(false)
  const wrapRef = useRef(null)

  useEffect(() => {
    if (!open) return
    const onDoc = e => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [open])

  const label = selectedMonth ? monthLabel(selectedMonth) : '—'

  return (
    <Wrap ref={wrapRef}>
      <PillSelect
        type="button"
        title="Report month"
        aria-expanded={open}
        disabled={disabled || !months.length}
        onClick={() => {
          if (disabled || !months.length) return
          setOpen(o => !o)
        }}
      >
        <I.cal />
        <Cap>Month</Cap>
        <Val>{label}</Val>
        <I.chev />
      </PillSelect>
      {open && months.length > 0 && (
        <Dropdown role="listbox" aria-label="Report months">
          <List>
            {[...months].reverse().map(ym => (
              <Row
                key={ym}
                type="button"
                $active={ym === selectedMonth}
                onClick={() => {
                  onSelectMonth(ym)
                  setOpen(false)
                }}
              >
                {monthLabel(ym)}
              </Row>
            ))}
          </List>
        </Dropdown>
      )}
    </Wrap>
  )
}

WipAiMonthSelect.propTypes = {
  months: PropTypes.arrayOf(PropTypes.string),
  selectedMonth: PropTypes.string,
  onSelectMonth: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
}

export default WipAiMonthSelect
